// RiskRegisterFindingsPanel — G3.7 review surface (2026-07-03, Layer 2).
//
// Surfaces PEG-RISK-REGISTER-FLOOR-001 so the architect can adjudicate a
// below-floor risk register HERE instead of the run silently hard-failing
// at the Phase-5 pre-export gate. The floor is a profile-derived formula;
// when the generation swarm honestly grounds fewer risks than it demands
// (and refuses to fabricate), the architect either:
//   * adds rows to phase3/risk-register.csv by hand (the finding clears
//     on refetch), or
//   * confirms complete-with-rationale — a waiver — which suppresses the
//     export-time finding (same shared waiver path as the data-dictionary
//     / threat-model / controls-coverage panels).
//
// Read-only otherwise: there's no "auto-add rows" fix because inventing
// risks is exactly what we do NOT want the pipeline to do.

import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { AlertTriangle, CheckCircle2, Info, Loader2, ShieldOff } from 'lucide-react'

import {
  getRiskRegisterFindings,
  RR_INVARIANT_ID,
  type RiskRegisterFindingsResponse,
} from '@/lib/riskRegisterFindingsApi'
import { WaiveFindingModal } from './WaiveFindingModal'

export interface RiskRegisterFindingsPanelProps {
  projectId: string
  runId: number
}

//: Formula breakdown keys → human labels. Diagnostic keys (leading "_")
//: are rendered separately so the architect sees the supply signal.
const BREAKDOWN_LABEL: Record<string, string> = {
  baseline: 'baseline',
  multi_tenant: 'multi-tenant',
  pii: 'PII',
  ai: 'AI output',
  external_integrations: 'integrations',
  regulated: 'regulated',
}

export function RiskRegisterFindingsPanel({
  projectId,
  runId,
}: RiskRegisterFindingsPanelProps) {
  const queryKey = ['risk-register-findings', projectId, runId]
  const { data, isLoading, error } = useQuery<RiskRegisterFindingsResponse>({
    queryKey,
    queryFn: () => getRiskRegisterFindings(projectId, runId),
  })

  const [waiving, setWaiving] = useState(false)

  const formulaChips = useMemo(() => {
    const bd = data?.finding?.breakdown ?? {}
    return Object.entries(bd)
      .filter(([k]) => !k.startsWith('_') && k in BREAKDOWN_LABEL)
      .map(([k, v]) => `${BREAKDOWN_LABEL[k]} +${v}`)
  }, [data])

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 p-4 text-sm text-slate-400">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading risk-register findings…
      </div>
    )
  }
  if (error) {
    return (
      <div className="rounded border border-red-800 bg-red-950/30 p-3 text-sm text-red-200">
        Failed to load risk-register findings:{' '}
        {String((error as Error).message)}
      </div>
    )
  }
  if (!data) return null

  if (!data.risk_register_present) {
    return (
      <div className="rounded border border-slate-700 bg-slate-900/50 p-3 text-sm text-slate-300">
        <div className="mb-1 flex items-center gap-2 text-slate-200">
          <Info className="h-4 w-4 text-sky-400" />
          Phase 3 hasn't shipped risk-register.csv yet
        </div>
        <p className="text-xs text-slate-400">
          The compliance agent authors{' '}
          <code className="rounded bg-slate-800 px-1 py-0.5">
            phase3/risk-register.csv
          </code>{' '}
          in Phase 3. Once it lands, this panel flags a register that
          falls below the profile-derived row floor.
        </p>
      </div>
    )
  }

  const finding = data.finding

  // Meets the floor (or waived) → clean.
  if (!finding || finding.waived) {
    return (
      <div className="rounded border border-emerald-800/40 bg-emerald-950/15 p-3 text-sm text-emerald-200">
        <div className="flex items-center gap-2">
          <CheckCircle2 className="h-4 w-4 text-emerald-400" />
          {finding?.waived
            ? 'Risk-register floor accepted by the architect.'
            : 'Risk register meets the profile floor.'}
        </div>
        {finding?.waived && finding.waiver_justification && (
          <p className="mt-1 text-xs text-emerald-300/80">
            Rationale: {finding.waiver_justification}
          </p>
        )}
      </div>
    )
  }

  return (
    <div className="space-y-3">
      <div className="rounded-md border border-amber-800/40 bg-slate-900/40 p-3">
        <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
          <span className="inline-flex items-center rounded border border-amber-700/50 bg-amber-950/30 px-1.5 py-0.5 text-[10.5px] font-medium text-amber-200">
            HIGH
          </span>
          <span className="inline-flex items-center gap-1 text-xs text-amber-300">
            <AlertTriangle className="h-3 w-3" />
            {finding.actual_rows} of {finding.floor} rows · {finding.deficit}{' '}
            below floor
          </span>
        </div>
        <div className="text-sm text-slate-200">
          The risk register grounds{' '}
          <span className="font-semibold">{finding.actual_rows}</span>{' '}
          distinct risk{finding.actual_rows === 1 ? '' : 's'}, but this
          system's profile expects at least{' '}
          <span className="font-semibold">{finding.floor}</span>. This is
          the honest floor for the profile — the swarm won't fabricate
          risks to hit it, so it's your call.
        </div>
        {formulaChips.length > 0 && (
          <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[10.5px] text-slate-400">
            <span className="uppercase tracking-wide text-slate-500">
              floor formula:
            </span>
            {formulaChips.map((c) => (
              <span
                key={c}
                className="rounded border border-slate-700 bg-slate-950/40 px-1.5 py-0.5 font-mono"
              >
                {c}
              </span>
            ))}
            {typeof finding.breakdown._groundable_anchors === 'number' && (
              <span className="rounded border border-slate-700 bg-slate-950/40 px-1.5 py-0.5 font-mono">
                anchors {finding.breakdown._groundable_anchors}
              </span>
            )}
          </div>
        )}

        <details className="mt-1.5 text-[10.5px] text-slate-500">
          <summary className="cursor-pointer select-none text-slate-400">
            Developer detail
          </summary>
          <div className="mt-1 text-slate-400">{finding.message}</div>
        </details>

        <div className="mt-2 flex flex-wrap items-center gap-2 border-t border-slate-800/50 pt-2">
          <button
            type="button"
            onClick={() => setWaiving(true)}
            className="inline-flex items-center gap-1 rounded border border-amber-700/40 bg-amber-950/20 px-2 py-1 text-[11px] font-medium text-amber-200 hover:bg-amber-900/30"
          >
            <ShieldOff className="h-3 w-3" /> Confirm complete / Waive
          </button>
        </div>
      </div>

      <div className="rounded border border-slate-800 bg-slate-900/40 p-3 text-xs text-slate-400">
        <div className="mb-1 flex items-center gap-2 text-slate-300">
          <CheckCircle2 className="h-4 w-4 text-emerald-400" />
          How to clear this
        </div>
        <p>
          <strong className="text-sky-200">More real risks exist?</strong>{' '}
          Add rows to{' '}
          <code className="rounded bg-slate-800 px-1 py-0.5">
            phase3/risk-register.csv
          </code>{' '}
          (each grounded in a real <code>C-###</code> / BR / SR / Charter
          anchor) and this finding clears on refresh.{' '}
          <strong className="text-amber-200">
            {finding.actual_rows} is genuinely complete?
          </strong>{' '}
          Click <strong>Confirm complete / Waive</strong> and record why —
          it lands in the audit trail and the pre-export gate stops
          blocking on it.
        </p>
      </div>

      {waiving && (
        <WaiveFindingModal
          projectId={projectId}
          runId={runId}
          invariantId={RR_INVARIANT_ID}
          findingKey={finding.finding_key}
          findingLabel={`Risk register: ${finding.actual_rows}/${finding.floor} rows`}
          refetchQueryKey={queryKey}
          onClose={() => setWaiving(false)}
        />
      )}
    </div>
  )
}
